// FILE: src/components/admin/dates.js
// Toronto calendar days for the WP-7 reports. The server counts days in
// America/Toronto (plan AD-3) and takes `from` / `to` as YYYY-MM-DD with `to`
// included, so the date pickers default to Toronto days too.
export const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const TIME_ZONE = "America/Toronto";
const DAY_MS = 24 * 60 * 60 * 1000;

/** The Toronto day of an instant as "YYYY-MM-DD". */
export function torontoDay(at = new Date()) {
  const d = at instanceof Date ? at : new Date(at);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** The Toronto day `days` before `at` (calendar arithmetic at noon, so DST never skips a day). */
export function torontoDaysAgo(days, at = new Date()) {
  const [y, m, d] = torontoDay(at).split("-").map(Number);
  const noon = Date.UTC(y, m - 1, d, 12) - days * DAY_MS;
  return new Date(noon).toISOString().slice(0, 10);
}

/** A valid "YYYY-MM-DD" or "". */
export function dayOrEmpty(value) {
  return DAY_RE.test(value || "") ? value : "";
}

/** "2026-10-07" → "7 Oct 2026" (the day itself, whatever the browser's zone). */
export function formatDay(day) {
  if (!DAY_RE.test(day || "")) return "—";
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-CA", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

/** Problems with a from/to pair, in words ("" when fine). */
export function rangeProblem(from, to, { required = true } = {}) {
  if (required && (!DAY_RE.test(from || "") || !DAY_RE.test(to || ""))) return "Enter both dates.";
  if (from && !DAY_RE.test(from)) return "Enter the start date as a full date.";
  if (to && !DAY_RE.test(to)) return "Enter the end date as a full date.";
  if (from && to && from > to) return "The start date must be on or before the end date.";
  return "";
}

/** Ready-made date ranges for the dashboard figures (Toronto days, `to` = today). */
export const RANGE_PRESETS = [
  { value: "all", label: "All time", days: null },
  { value: "7d", label: "Last 7 days", days: 7 },
  { value: "30d", label: "Last 30 days", days: 30 },
  { value: "12m", label: "Last 12 months", days: 365 }
];

/** A preset → { from, to } (empty for all time). */
export function presetRange(value, at = new Date()) {
  const preset = RANGE_PRESETS.find((p) => p.value === value);
  if (!preset || !preset.days) return {};
  return { from: torontoDaysAgo(preset.days - 1, at), to: torontoDay(at) };
}
